import { PLATFORM_HEIGHT } from './battle-constants.js';
import { THEME_CONFIG } from './battle-constants.js';
import { createThemeParticles } from './battle-particles.js';

// Image cache for theme backgrounds
const themeImageCache = {};

// Export theme-related functions
export {
  PLATFORM_HEIGHT,
  initBattleEnvironment,
  drawThemeEffects,
  generateTerrainPlatforms,
  drawPlatform,
  drawThemeBackground,
  drawSpecialThemeEffects,
  preloadThemeBackgrounds,
  drawRoundedRectangle
};

/**
 * Preloads all theme background images
 */
function preloadThemeBackgrounds() {
  Object.values(THEME_CONFIG).forEach(config => {
    if (config.backgroundImage && !themeImageCache[config.backgroundImage]) {
      const img = new Image();
      img.onload = () => {
        console.log(`[Theme] Preloaded background: ${config.backgroundImage}`);
      };
      img.onerror = () => {
        console.warn(`[Theme] Failed to preload background: ${config.backgroundImage}`);
        themeImageCache[config.backgroundImage] = null;
      };
      img.src = config.backgroundImage;
      themeImageCache[config.backgroundImage] = img;
    }
  });
}

/**
 * Initializes the battle environment and theme
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {Object} - Initialized battle theme
 */
function initBattleEnvironment(ctx, width, height) {
  // This will be used to draw theme-specific background elements
  const battleTheme = {
    name: ['volcano', 'ice', 'jungle', 'space', 'candy', 'underwater'][Math.floor(Math.random() * 6)],
    particles: [],
    hazards: []
  };
  
  // Preload theme background images
  preloadThemeBackgrounds();
  
  // Create initial particles for the theme
  createThemeParticles(battleTheme, width, height);
  
  return battleTheme;
}

/**
 * Initializes theme-specific elements
 * @param {Object} theme - The current battle theme
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
function initThemeElements(theme, width, height) {
  // Create theme-specific elements such as hazards and special objects
  switch (theme.name) {
    case 'volcano':
      // Add lava hazards
      theme.hazards = [{
        type: 'lava',
        x: width / 2,
        y: height - 50,
        width: width,
        height: 50
      }];
      break;
    case 'ice':
      // Add slippery platforms
      theme.slipperyFactor = 0.98;
      break;
    case 'space':
      // Add floating debris
      for (let i = 0; i < 5; i++) {
        theme.hazards.push({
          type: 'asteroid',
          x: Math.random() * width,
          y: Math.random() * height / 2,
          size: 20 + Math.random() * 30,
          velocityX: (Math.random() - 0.5) * 2,
          velocityY: Math.random() * 1
        });
      }
      break;
  }
}

/**
 * Returns appropriate particle colors for the current theme
 * @param {string} themeName - Name of the current theme
 * @returns {Array} - Array of color strings
 */
function getThemeParticleColor(themeName) {
  // Return theme-appropriate colors
  const colors = {
    volcano: ['#ff4500', '#ff8c00', '#ffaa00'],
    ice: ['#aaf', '#ccf', '#fff'],
    jungle: ['#0c0', '#0a0', '#8c8'],
    space: ['#fff', '#aaf', '#faf'],
    candy: ['#f0f', '#0ff', '#ff0'],
    underwater: ['#0cf', '#08f', '#0af']
  };
  
  return colors[themeName] || ['#fff'];
}

/**
 * Draws theme-specific visual effects
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} battleTheme - Battle theme object
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} cameraXOffset - Camera X offset for panning
 * @param {number} cameraYOffset - Camera Y offset for panning
 */
function drawThemeEffects(ctx, battleTheme, width, height, cameraXOffset = 0, cameraYOffset = 0) {
  if (!battleTheme || !battleTheme.name) return;
  
  ctx.save();
  
  // Draw background image with parallax effect
  const parallaxFactor = 0.3; // Background moves 30% of camera movement
  const parallaxOffsetX = cameraXOffset * parallaxFactor;
  const parallaxOffsetY = cameraYOffset * parallaxFactor;
  
  // Draw the background for the visible area plus some padding
  const padding = Math.max(width, height);
  const backgroundX = cameraXOffset - padding;
  const backgroundY = cameraYOffset - padding;
  const backgroundWidth = width + (padding * 2);
  const backgroundHeight = height + (padding * 2);
  
  // Draw theme background with parallax offset
  drawThemeBackgroundWithParallax(
    ctx, 
    battleTheme.name, 
    backgroundX, 
    backgroundY, 
    backgroundWidth, 
    backgroundHeight,
    parallaxOffsetX,
    parallaxOffsetY
  );
  
  // Draw foreground effects that don't move with camera (overlay effects)
  drawSpecialThemeEffects(ctx, battleTheme, width, height);
  
  ctx.restore();
}

/**
 * Updates theme particle positions
 * @param {Object} theme - The current battle theme
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @private
 */
function updateThemeParticles(theme, width, height) {
  // Update all theme particles
  theme.particles.forEach(particle => {
    // Apply movement
    particle.x += particle.velocityX;
    particle.y += particle.velocityY;
    
    // Wrap around edges
    if (particle.x < 0) particle.x = width;
    if (particle.x > width) particle.x = 0;
    if (particle.y < 0) particle.y = height;
    if (particle.y > height) particle.y = 0;
  });
}

/**
 * Draws the theme background
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {string} themeName - The current battle theme name
 * @param {number} tileX - X position for this background tile
 * @param {number} tileY - Y position for this background tile
 * @param {number} width - Tile width
 * @param {number} height - Tile height
 */
function drawThemeBackground(ctx, themeName, tileX, tileY, width, height) {
  // Get theme config for background image
  const themeConfig = THEME_CONFIG[themeName] || THEME_CONFIG.default;
  const backgroundImagePath = themeConfig.backgroundImage;
  
  // Always draw gradient first as fallback
  drawGradientBackground(ctx, themeName, tileX, tileY, width, height);
  
  // Try to load and draw the background image
  if (backgroundImagePath) {
    let bgImage = themeImageCache[backgroundImagePath];
    
    if (!bgImage) {
      // Start loading the image
      bgImage = new Image();
      bgImage.onload = () => {
        console.log(`[Theme] Loaded background image: ${backgroundImagePath}`);
      };
      bgImage.onerror = () => {
        console.warn(`[Theme] Failed to load background image: ${backgroundImagePath}`);
        themeImageCache[backgroundImagePath] = null; // Mark as failed
      };
      bgImage.src = backgroundImagePath;
      themeImageCache[backgroundImagePath] = bgImage;
    }
    
    // If image is loaded, tile it across the background area
    if (bgImage && bgImage.complete && bgImage.naturalWidth > 0) {
      drawTiledBackground(ctx, bgImage, tileX, tileY, width, height);
    }
  }
  
  // Draw background layers with theme-specific elements
  drawBackgroundLayers(ctx, { name: themeName }, tileX, tileY, width, height);
}

/**
 * Draws gradient background as fallback
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {string} themeName - The current battle theme name
 * @param {number} tileX - X position for this background tile
 * @param {number} tileY - Y position for this background tile
 * @param {number} width - Tile width
 * @param {number} height - Tile height
 */
function drawGradientBackground(ctx, themeName, tileX, tileY, width, height) {
  const gradient = ctx.createLinearGradient(tileX, tileY, tileX, tileY + height);
  
  switch (themeName) {
    case 'volcano':
      gradient.addColorStop(0, '#4a0e0e');
      gradient.addColorStop(0.5, '#8b1a1a');
      gradient.addColorStop(1, '#d32f2f');
      break;
    case 'ice':
      gradient.addColorStop(0, '#e3f2fd');
      gradient.addColorStop(0.5, '#bbdefb');
      gradient.addColorStop(1, '#90caf9');
      break;
    case 'jungle':
      gradient.addColorStop(0, '#1b5e20');
      gradient.addColorStop(0.5, '#2e7d32');
      gradient.addColorStop(1, '#43a047');
      break;
    case 'space':
      gradient.addColorStop(0, '#0d1421');
      gradient.addColorStop(0.3, '#1a237e');
      gradient.addColorStop(1, '#303f9f');
      break;
    case 'candy':
      gradient.addColorStop(0, '#f8bbd9');
      gradient.addColorStop(0.5, '#f48fb1');
      gradient.addColorStop(1, '#f06292');
      break;
    case 'underwater':
      gradient.addColorStop(0, '#006064');
      gradient.addColorStop(0.5, '#00838f');
      gradient.addColorStop(1, '#0097a7');
      break;
    default:
      gradient.addColorStop(0, '#263238');
      gradient.addColorStop(1, '#455a64');
  }
  
  ctx.fillStyle = gradient;
  ctx.fillRect(tileX, tileY, width, height);
}

/**
 * Draws a tiled background image
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {HTMLImageElement} image - The background image to tile
 * @param {number} tileX - X position for this background tile
 * @param {number} tileY - Y position for this background tile
 * @param {number} width - Tile width
 * @param {number} height - Tile height
 */
function drawTiledBackground(ctx, image, tileX, tileY, width, height) {
  // Calculate how many times we need to repeat the image to fill the tile
  const imageWidth = image.naturalWidth;
  const imageHeight = image.naturalHeight;
  
  // Scale the background image so its height matches the virtual arena height (1600px)
  const virtualArenaHeight = 1600;
  const scaleFactor = virtualArenaHeight / imageHeight;
  const scaledWidth = imageWidth * scaleFactor;
  const scaledHeight = virtualArenaHeight; // Always 1600px high
  
  // Calculate tile counts needed to cover the tile area
  const tilesX = Math.ceil(width / scaledWidth) + 1;
  const tilesY = Math.ceil(height / scaledHeight) + 1;
  
  // Draw tiled background with good opacity to make it prominent
  ctx.save();
  ctx.globalAlpha = 0.85; // More prominent background images
  
  // Calculate the offset to align with arena coordinates (top-left of virtual arena is 0,0)
  const arenaStartX = 0;
  const arenaStartY = 0;
  
  // Find the starting tile position relative to arena coordinates
  const startTileX = Math.floor(tileX / scaledWidth);
  const startTileY = Math.floor(tileY / scaledHeight);
  
  for (let x = startTileX; x < startTileX + tilesX; x++) {
    for (let y = startTileY; y < startTileY + tilesY; y++) {
      const drawX = x * scaledWidth;
      const drawY = y * scaledHeight;
      
      // Only draw if this tile intersects with our current tile area
      if (drawX < tileX + width && drawX + scaledWidth > tileX &&
          drawY < tileY + height && drawY + scaledHeight > tileY) {
        ctx.drawImage(image, drawX, drawY, scaledWidth, scaledHeight);
      }
    }
  }
  
  ctx.restore();
}

/**
 * Draws background layers for the current theme
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} theme - The current battle theme
 * @param {number} tileX - X position for this background tile
 * @param {number} tileY - Y position for this background tile
 * @param {number} width - Tile width
 * @param {number} height - Tile height
 * @private
 */
function drawBackgroundLayers(ctx, theme, tileX, tileY, width, height) {
  // Draw theme-specific background elements positioned within the tile
  // Reduce opacity so they don't compete with background images
  ctx.save();
  ctx.globalAlpha = 0.3; // Make programmatic elements subtle
  
  switch (theme.name) {
    case 'volcano':
      // Draw volcano and lava
      drawVolcanoBackground(ctx, tileX, tileY, width, height);
      break;
    case 'ice':
      // Draw snow-capped mountains
      drawIceBackground(ctx, tileX, tileY, width, height);
      break;
    case 'jungle':
      // Draw trees and foliage
      drawJungleBackground(ctx, tileX, tileY, width, height);
      break;
    case 'space':
      // Draw stars and planets
      drawSpaceBackground(ctx, tileX, tileY, width, height);
      break;
    case 'candy':
      // Draw candy elements
      drawCandyBackground(ctx, tileX, tileY, width, height);
      break;
    case 'underwater':
      // Draw underwater elements
      drawUnderwaterBackground(ctx, tileX, tileY, width, height);
      break;
  }
  
  ctx.restore();
}

/**
 * Draws special theme-specific effects
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} theme - The current battle theme
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
function drawSpecialThemeEffects(ctx, theme, width, height) {
  // Draw theme-specific special effects
  switch (theme.name) {
    case 'volcano':
      drawLavaEffect(ctx, theme, width, height);
      break;
    case 'space':
      drawGridEffect(ctx, theme, width, height);
      break;
  }
}

/**
 * Draws lava effects for volcano theme
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} theme - The current battle theme
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @private
 */
function drawLavaEffect(ctx, theme, width, height) {
  // Removed: this used to paint a 50px lava strip at the bottom of the
  // canvas (viewport coords, not world coords). With the camera, the
  // strip floated at the viewport bottom regardless of camera position —
  // it looked like a bugged red bar drifting across the arena. The
  // volcano theme already has lava-colored platforms; the strip added
  // confusion, not atmosphere.
}

/**
 * Draws a grid effect for space theme
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} theme - The current battle theme
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @private
 */
function drawGridEffect(ctx, theme, width, height) {
  // Draw a subtle grid effect for space theme
  ctx.strokeStyle = 'rgba(100, 100, 255, 0.2)';
  ctx.lineWidth = 1;
  
  // Horizontal lines
  for (let y = 0; y < height; y += 40) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }
  
  // Vertical lines
  for (let x = 0; x < width; x += 40) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
}

/**
 * Generates terrain platforms for the battle arena
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {number} canvasWidth - Viewport width
 * @param {number} canvasHeight - Viewport height
 * @param {number} virtualArenaWidth - Virtual arena width (larger than canvas)
 * @param {number} virtualArenaHeight - Virtual arena height (larger than canvas)
 * @returns {Array} - Array of platform objects
 */
function generateTerrainPlatforms(ctx, canvasWidth, canvasHeight, virtualArenaWidth = canvasWidth * 3, virtualArenaHeight = canvasHeight * 2) {
  const platforms = [];
  
  // Create ground platform that spans the entire virtual arena
  const groundY = virtualArenaHeight - PLATFORM_HEIGHT;
  platforms.push({
    id: 'ground',
    type: 'ground',
    x: virtualArenaWidth / 2, // Center position
    y: groundY,
    width: virtualArenaWidth, // Span entire virtual arena width
    height: PLATFORM_HEIGHT
  });
  
  // Define distinct platform levels for varied gameplay. Counts trimmed
  // from the previous denser layout (15 → 8 floating) so the arena reads
  // less cluttered and gives characters more room to manoeuvre.
  const platformLevels = [
    { heightRange: [groundY - 200, groundY - 150], count: 2, widthRange: [200, 300] },     // Low platforms
    { heightRange: [groundY - 400, groundY - 300], count: 2, widthRange: [160, 260] },     // Mid-low platforms
    { heightRange: [groundY - 650, groundY - 500], count: 2, widthRange: [130, 220] },     // Mid platforms
    { heightRange: [groundY - 900, groundY - 750], count: 1, widthRange: [110, 180] },     // Mid-high platforms
    { heightRange: [groundY - 1200, groundY - 1000], count: 1, widthRange: [100, 150] },   // High platforms
  ];
  
  let platformId = 1;
  
  // Generate platforms for each level
  platformLevels.forEach((level, levelIndex) => {
    for (let i = 0; i < level.count; i++) {
      // Distribute platforms across the width with some spacing
      const sectionWidth = virtualArenaWidth / level.count;
      const baseX = (i + 0.5) * sectionWidth;
      
      // Add some random offset to prevent too uniform spacing
      const xOffset = (Math.random() - 0.5) * sectionWidth * 0.4;
      const platformX = Math.max(150, Math.min(virtualArenaWidth - 150, baseX + xOffset));
      
      // Random height within the level's range
      const minHeight = level.heightRange[0];
      const maxHeight = level.heightRange[1];
      const platformY = minHeight + (Math.random() * (maxHeight - minHeight));
      
      // Random width within the level's range
      const minWidth = level.widthRange[0];
      const maxWidth = level.widthRange[1];
      const platformWidth = minWidth + (Math.random() * (maxWidth - minWidth));
      
      platforms.push({
        id: `platform_${platformId}`,
        type: 'floating',
        x: platformX,
        y: platformY,
        width: platformWidth,
        height: PLATFORM_HEIGHT,
        level: levelIndex // Track which level this platform belongs to
      });
      
      platformId++;
    }
  });
  
  // Add some special "bridge" platforms that connect different areas
  const bridgeCount = 1;
  for (let i = 0; i < bridgeCount; i++) {
    const bridgeX = virtualArenaWidth * (0.3 + (i * 0.4)); // Spread bridges across arena
    const bridgeY = groundY - 300 - (i * 200); // Stagger bridge heights
    const bridgeWidth = 300 + (Math.random() * 200); // Longer bridges
    
    platforms.push({
      id: `bridge_${i}`,
      type: 'bridge',
      x: bridgeX,
      y: bridgeY,
      width: bridgeWidth,
      height: PLATFORM_HEIGHT,
      level: -1 // Special bridge level
    });
  }
  
  // Add some small "stepping stone" platforms for vertical traversal
  const steppingStoneCount = 1;
  for (let i = 0; i < steppingStoneCount; i++) {
    const stoneX = virtualArenaWidth * (0.2 + (i * 0.3));
    const stoneY = groundY - (200 + (i * 150)); // Create ascending/descending patterns
    const stoneWidth = 80 + (Math.random() * 40); // Small platforms
    
    platforms.push({
      id: `stone_${i}`,
      type: 'stepping_stone',
      x: stoneX,
      y: stoneY,
      width: stoneWidth,
      height: PLATFORM_HEIGHT,
      level: -2 // Special stepping stone level
    });
  }
  
  console.log(`[Theme] Generated ${platforms.length} platforms across ${platformLevels.length} levels`);
  
  return platforms;
}

/**
 * Draws a platform with theme-specific styling
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} platform - The platform to draw
 */
function drawPlatform(ctx, platform) {
  // Draw platform depending on its type
  let platformColor;
  let platformStrokeColor;
  let platformStyle = 'normal'; // normal, bridge, stepping_stone
  
  // Determine base colors based on platform type
  if (platform.type === 'bridge') {
    platformColor = '#8B4513'; // Brown for bridges
    platformStrokeColor = '#654321';
    platformStyle = 'bridge';
  } else if (platform.type === 'stepping_stone') {
    platformColor = '#696969'; // Dark gray for stepping stones
    platformStrokeColor = '#2F4F4F';
    platformStyle = 'stepping_stone';
  } else {
    // Standard platform colors based on theme (fallback to default if no theme specified)
    switch(platform.type) {
      case 'volcano':
        platformColor = '#8B4513'; // Brown
        platformStrokeColor = '#A52A2A'; // Darker brown
        break;
      case 'ice':
        platformColor = '#A5F2F3'; // Light blue
        platformStrokeColor = '#FFFFFF'; // White
        break;
      case 'jungle':
        platformColor = '#228B22'; // Forest green
        platformStrokeColor = '#006400'; // Dark green
        break;
      case 'space':
        platformColor = '#4B0082'; // Indigo
        platformStrokeColor = '#9370DB'; // Medium purple
        break;
      case 'candy':
        platformColor = '#FF69B4'; // Hot pink
        platformStrokeColor = '#FF1493'; // Deep pink
        break;
      case 'underwater':
        platformColor = '#00CED1'; // Dark turquoise
        platformStrokeColor = '#5F9EA0'; // Cadet blue
        break;
      default:
        platformColor = '#555555'; // Default gray
        platformStrokeColor = '#333333'; // Darker gray
    }
  }
  
  // Draw platform with different styles based on type
  const x = platform.x - platform.width / 2;
  const y = platform.y;
  const radius = platformStyle === 'stepping_stone' ? 5 : 10; // Smaller radius for stepping stones
  
  ctx.fillStyle = platformColor;
  ctx.strokeStyle = platformStrokeColor;
  ctx.lineWidth = platformStyle === 'bridge' ? 3 : 2; // Thicker lines for bridges
  
  if (platformStyle === 'stepping_stone') {
    // Draw stepping stones as circles or rounded squares
    if (platform.width < 100) {
      // Small circular stepping stones
      ctx.beginPath();
      ctx.arc(platform.x, y + platform.height/2, platform.width/2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else {
      // Rounded square stepping stones
      drawRoundedRectangle(ctx, x, y, platform.width, platform.height, radius);
    }
  } else if (platformStyle === 'bridge') {
    // Draw bridges with support posts
    drawRoundedRectangle(ctx, x, y, platform.width, platform.height, radius);

    // Add bridge support posts
    ctx.fillStyle = platformStrokeColor;
    const postWidth = 8;
    const postCount = Math.floor(platform.width / 80); // One post every 80 pixels

    for (let i = 1; i <= postCount; i++) {
      const postX = x + (i * platform.width / (postCount + 1)) - postWidth/2;
      const postHeight = 30;
      ctx.fillRect(postX, y + platform.height, postWidth, postHeight);
    }
  } else {
    // Draw normal platforms with a vertical gradient (lighter top → darker
    // bottom) so the surface reads 3D instead of as a flat rectangle. Then
    // overlay a thin highlight on the top edge + soft shadow on the bottom
    // for depth — same trick the league badges + leaderboard cards use.
    const grad = ctx.createLinearGradient(x, y, x, y + platform.height);
    grad.addColorStop(0, lightenColor(platformColor, 0.25));
    grad.addColorStop(0.55, platformColor);
    grad.addColorStop(1, darkenColor(platformColor, 0.25));
    ctx.fillStyle = grad;
    drawRoundedRectangle(ctx, x, y, platform.width, platform.height, radius);

    // Top highlight band: thin strip of high-alpha white right at the top
    // edge to imply a polished surface catching light from above.
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    drawRoundedRectangle(ctx, x + 4, y + 2, platform.width - 8, Math.max(3, platform.height * 0.22), Math.max(2, radius - 2));
    ctx.restore();

    // Bottom shadow strip — darkens the underside so the platform appears
    // to cast onto itself.
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
    drawRoundedRectangle(ctx, x + 4, y + platform.height - Math.max(3, platform.height * 0.2), platform.width - 8, Math.max(3, platform.height * 0.18), Math.max(2, radius - 2));
    ctx.restore();
  }
  
  // Add theme-specific details
  switch(platform.type) {
    case 'ice':
      // Add shine to ice platforms
      const gradient = ctx.createLinearGradient(x, y, x + platform.width, y + platform.height);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0.5)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.rect(x, y, platform.width, platform.height / 2);
      ctx.fill();
      break;
    case 'candy':
      // Stripes removed — the new 3D platform gradient already reads
      // pink-on-pink and the vertical stripes were visually busy.
      break;
    case 'space':
      // Add glowing effect to space platforms
      ctx.shadowColor = '#9370DB';
      ctx.shadowBlur = 10;
      drawRoundedRectangle(ctx, x, y, platform.width, platform.height, radius);
      ctx.shadowBlur = 0; // Reset shadow
      break;
  }
  
  // Add level indicators (small dots) for debugging/visual feedback
  if (platform.level !== undefined && platform.level >= 0) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    for (let i = 0; i <= platform.level && i < 6; i++) {
      const dotX = x + 10 + (i * 8);
      const dotY = y + 5;
      ctx.beginPath();
      ctx.arc(dotX, dotY, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Helper function to draw rounded rectangles
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {number} x - X position
 * @param {number} y - Y position  
 * @param {number} width - Width
 * @param {number} height - Height
 * @param {number} radius - Corner radius
 */
// Lighten / darken a hex (#RRGGBB) or rgb()/rgba() color by `amount` (0..1).
// Returns an rgba() string. Used by drawPlatform to build a 3D-ish gradient
// from the platform's base color without forcing every theme to ship
// pre-mixed shades.
function _parseColor(c) {
    if (!c) return [128, 128, 128, 1];
    if (c.startsWith('#')) {
        const h = c.slice(1);
        const hex = (h.length === 3) ? h.split('').map(x => x + x).join('') : h;
        return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16), 1];
    }
    const m = c.match(/rgba?\(([^)]+)\)/);
    if (m) {
        const parts = m[1].split(',').map(s => parseFloat(s.trim()));
        return [parts[0] || 0, parts[1] || 0, parts[2] || 0, parts[3] == null ? 1 : parts[3]];
    }
    return [128, 128, 128, 1];
}
function lightenColor(c, amount) {
    const [r, g, b, a] = _parseColor(c);
    const f = Math.max(0, Math.min(1, amount));
    return `rgba(${Math.round(r + (255 - r) * f)}, ${Math.round(g + (255 - g) * f)}, ${Math.round(b + (255 - b) * f)}, ${a})`;
}
function darkenColor(c, amount) {
    const [r, g, b, a] = _parseColor(c);
    const f = Math.max(0, Math.min(1, amount));
    return `rgba(${Math.round(r * (1 - f))}, ${Math.round(g * (1 - f))}, ${Math.round(b * (1 - f))}, ${a})`;
}

function drawRoundedRectangle(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  
  ctx.fill();
  ctx.stroke();
}

// Theme-specific background drawing functions (private functions)
function drawVolcanoBackground(ctx, tileX, tileY, width, height) {
  // Draw volcano
  ctx.fillStyle = '#8B4513';
  ctx.beginPath();
  ctx.moveTo(tileX, tileY + height);
  ctx.lineTo(tileX + width * 0.2, tileY + height);
  ctx.lineTo(tileX + width * 0.4, tileY + height * 0.6);
  ctx.lineTo(tileX + width * 0.6, tileY + height * 0.6);
  ctx.lineTo(tileX + width * 0.8, tileY + height);
  ctx.lineTo(tileX + width, tileY + height);
  ctx.closePath();
  ctx.fill();
  
  // Draw crater
  ctx.fillStyle = '#ff4500';
  ctx.beginPath();
  ctx.arc(tileX + width * 0.5, tileY + height * 0.6, width * 0.1, 0, Math.PI, true);
  ctx.fill();
}

function drawIceBackground(ctx, tileX, tileY, width, height) {
  // Draw mountains
  ctx.fillStyle = '#FFFFFF';
  
  // Left mountain
  ctx.beginPath();
  ctx.moveTo(tileX, tileY + height);
  ctx.lineTo(tileX + width * 0.3, tileY + height * 0.5);
  ctx.lineTo(tileX + width * 0.4, tileY + height);
  ctx.closePath();
  ctx.fill();
  
  // Right mountain
  ctx.beginPath();
  ctx.moveTo(tileX + width * 0.6, tileY + height);
  ctx.lineTo(tileX + width * 0.8, tileY + height * 0.4);
  ctx.lineTo(tileX + width, tileY + height);
  ctx.closePath();
  ctx.fill();
  
  // Snow
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.fillRect(tileX, tileY + height - 30, width, 30);
}

function drawJungleBackground(ctx, tileX, tileY, width, height) {
  // Draw distant trees
  for (let i = 0; i < width; i += 50) {
    const treeHeight = 100 + Math.random() * 50;
    ctx.fillStyle = '#006400';
    ctx.beginPath();
    ctx.rect(tileX + i, tileY + height - treeHeight, 20, treeHeight);
    ctx.fill();
    
    // Tree top
    ctx.fillStyle = '#008000';
    ctx.beginPath();
    ctx.arc(tileX + i + 10, tileY + height - treeHeight, 25, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSpaceBackground(ctx, tileX, tileY, width, height) {
  // Draw stars (already handled by particles)
  
  // Draw distant planet
  const planetX = tileX + width * 0.8;
  const planetY = tileY + height * 0.2;
  const planetRadius = width * 0.1;
  
  // Planet body
  const gradient = ctx.createRadialGradient(
    planetX, planetY, 0,
    planetX, planetY, planetRadius
  );
  gradient.addColorStop(0, '#FF5555');
  gradient.addColorStop(1, '#AA0000');
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(planetX, planetY, planetRadius, 0, Math.PI * 2);
  ctx.fill();
  
  // Planet ring
  ctx.strokeStyle = 'rgba(255, 200, 100, 0.5)';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.ellipse(planetX, planetY, planetRadius * 1.5, planetRadius * 0.3, Math.PI / 4, 0, Math.PI * 2);
  ctx.stroke();
}

function drawCandyBackground(ctx, tileX, tileY, width, height) {
  // Candy-cane background stripes removed — they cut visually across the
  // entire arena and clashed with the lollipop foreground.

  // Draw some lollipops
  for (let i = 0; i < 3; i++) {
    const x = tileX + (i + 1) * width / 4;
    const y = tileY + height * 0.6;
    const radius = 30;
    
    // Lollipop stick
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.rect(x - 5, y, 10, tileY + height - y);
    ctx.fill();
    
    // Lollipop head
    const colors = ['#FF00FF', '#00FFFF', '#FFFF00'];
    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Spiral pattern
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    ctx.beginPath();
    for (let j = 0; j < 5; j++) {
      const angle = j * Math.PI / 2.5;
      const spiralX = x + Math.cos(angle) * radius * (j / 5);
      const spiralY = y + Math.sin(angle) * radius * (j / 5);
      
      if (j === 0) {
        ctx.moveTo(spiralX, spiralY);
      } else {
        ctx.lineTo(spiralX, spiralY);
      }
    }
    ctx.stroke();
  }
}

function drawUnderwaterBackground(ctx, tileX, tileY, width, height) {
  // Draw wavy water surface
  ctx.fillStyle = 'rgba(0, 100, 200, 0.5)';
  ctx.beginPath();
  ctx.moveTo(tileX, tileY + 50);
  
  for (let i = 0; i < width; i += 20) {
    const heightVar = Math.sin(i / 30) * 10;
    ctx.lineTo(tileX + i, tileY + 30 + heightVar);
  }
  
  ctx.lineTo(tileX + width, tileY + 50);
  ctx.lineTo(tileX + width, tileY);
  ctx.lineTo(tileX, tileY);
  ctx.closePath();
  ctx.fill();
  
  // Draw seaweed
  for (let i = 0; i < width; i += 100) {
    const time = Date.now() / 1000;
    ctx.strokeStyle = '#00AA00';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(tileX + i, tileY + height);
    
    for (let j = 0; j < 5; j++) {
      const waveX = tileX + i + Math.sin(time + j) * 15;
      const waveY = tileY + height - j * 30;
      ctx.lineTo(waveX, waveY);
    }
    
    ctx.stroke();
  }
}

/**
 * Draws theme background with parallax effect and arena alignment
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {string} themeName - The current battle theme name
 * @param {number} viewX - X position of the view area
 * @param {number} viewY - Y position of the view area
 * @param {number} viewWidth - Width of the view area
 * @param {number} viewHeight - Height of the view area
 * @param {number} parallaxOffsetX - Parallax X offset
 * @param {number} parallaxOffsetY - Parallax Y offset
 */
function drawThemeBackgroundWithParallax(ctx, themeName, viewX, viewY, viewWidth, viewHeight, parallaxOffsetX, parallaxOffsetY) {
  // Get theme config for background image
  const themeConfig = THEME_CONFIG[themeName] || THEME_CONFIG.default;
  const backgroundImagePath = themeConfig.backgroundImage;
  
  // Always draw gradient first as fallback
  drawGradientBackground(ctx, themeName, viewX, viewY, viewWidth, viewHeight);
  
  // Try to load and draw the background image
  if (backgroundImagePath) {
    let bgImage = themeImageCache[backgroundImagePath];
    
    if (!bgImage) {
      // Start loading the image
      bgImage = new Image();
      bgImage.onload = () => {
        console.log(`[Theme] Loaded background image: ${backgroundImagePath}`);
      };
      bgImage.onerror = () => {
        console.warn(`[Theme] Failed to load background image: ${backgroundImagePath}`);
        themeImageCache[backgroundImagePath] = null; // Mark as failed
      };
      bgImage.src = backgroundImagePath;
      themeImageCache[backgroundImagePath] = bgImage;
    }
    
    // If image is loaded, tile it with proper arena alignment
    if (bgImage && bgImage.complete && bgImage.naturalWidth > 0) {
      drawArenaAlignedBackground(ctx, bgImage, viewX, viewY, viewWidth, viewHeight, parallaxOffsetX, parallaxOffsetY);
    }
  }
  
  // Draw background layers with theme-specific elements (at reduced opacity)
  ctx.save();
  ctx.globalAlpha = 0.3; // Make programmatic elements subtle
  drawBackgroundLayersInView(ctx, { name: themeName }, viewX, viewY, viewWidth, viewHeight);
  ctx.restore();
}

/**
 * Draws background image tiles aligned with the arena coordinate system
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {HTMLImageElement} image - The background image to tile
 * @param {number} viewX - X position of the view area
 * @param {number} viewY - Y position of the view area
 * @param {number} viewWidth - Width of the view area
 * @param {number} viewHeight - Height of the view area
 * @param {number} parallaxOffsetX - Parallax X offset
 * @param {number} parallaxOffsetY - Parallax Y offset
 */
function drawArenaAlignedBackground(ctx, image, viewX, viewY, viewWidth, viewHeight, parallaxOffsetX, parallaxOffsetY) {
  const imageWidth = image.naturalWidth;
  const imageHeight = image.naturalHeight;
  
  // Scale the background image so its height matches the virtual arena height (1600px)
  const virtualArenaHeight = 1600;
  const scaleFactor = virtualArenaHeight / imageHeight;
  const scaledWidth = imageWidth * scaleFactor;
  const scaledHeight = virtualArenaHeight; // Always 1600px high
  
  ctx.save();
  ctx.globalAlpha = 0.85; // More prominent background images
  
  // Calculate the range of tiles we need to draw to cover the view area
  // Apply parallax offset to the tile positioning
  const startTileX = Math.floor((viewX - parallaxOffsetX) / scaledWidth) - 1;
  const endTileX = Math.ceil((viewX + viewWidth - parallaxOffsetX) / scaledWidth) + 1;
  const startTileY = Math.floor((viewY - parallaxOffsetY) / scaledHeight) - 1;
  const endTileY = Math.ceil((viewY + viewHeight - parallaxOffsetY) / scaledHeight) + 1;
  
  // Draw the background tiles
  for (let tileX = startTileX; tileX <= endTileX; tileX++) {
    for (let tileY = startTileY; tileY <= endTileY; tileY++) {
      const drawX = (tileX * scaledWidth) + parallaxOffsetX;
      const drawY = (tileY * scaledHeight) + parallaxOffsetY;
      
      ctx.drawImage(image, drawX, drawY, scaledWidth, scaledHeight);
    }
  }
  
  ctx.restore();
}

/**
 * Draws background layers for a specific view area
 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
 * @param {Object} theme - The current battle theme
 * @param {number} viewX - X position of the view area
 * @param {number} viewY - Y position of the view area
 * @param {number} viewWidth - Width of the view area
 * @param {number} viewHeight - Height of the view area
 */
function drawBackgroundLayersInView(ctx, theme, viewX, viewY, viewWidth, viewHeight) {
  switch (theme.name) {
    case 'volcano':
      drawVolcanoBackground(ctx, viewX, viewY, viewWidth, viewHeight);
      break;
    case 'ice':
      drawIceBackground(ctx, viewX, viewY, viewWidth, viewHeight);
      break;
    case 'jungle':
      drawJungleBackground(ctx, viewX, viewY, viewWidth, viewHeight);
      break;
    case 'space':
      drawSpaceBackground(ctx, viewX, viewY, viewWidth, viewHeight);
      break;
    case 'candy':
      drawCandyBackground(ctx, viewX, viewY, viewWidth, viewHeight);
      break;
    case 'underwater':
      drawUnderwaterBackground(ctx, viewX, viewY, viewWidth, viewHeight);
      break;
  }
}